// Status timeline (DESIGN.md 10.5): vertical, most recent first, <ol> for a11y.
// The rail dot carries a small status icon (color + icon, never color alone).

import type { FeedbackStatus, TimelineEntry } from '../lib/types';
import { STATUS_LABEL } from '../lib/types';
import type { Icon } from '../lib/icons';
import { absoluteTime } from '../lib/format';
import { toCustomerCopy } from '../lib/customerCopy';
import { motion } from 'motion/react';
import { useRevealStagger } from '../lib/motion';
import {
  PaperPlane,
  MagnifyingGlass,
  ChatCircleDots,
  SpinnerGap,
  CheckCircle,
  Check,
  ArrowCounterClockwise,
} from '../lib/icons';

const DOT_ICON: Record<FeedbackStatus, Icon> = {
  submitted: PaperPlane,
  under_review: MagnifyingGlass,
  awaiting_info: ChatCircleDots,
  in_progress: SpinnerGap,
  resolved: CheckCircle,
  confirmed: Check,
  reopened: ArrowCounterClockwise,
};

const DOT_COLOR: Record<FeedbackStatus, string> = {
  submitted: 'var(--text-2)',
  under_review: 'var(--accent)',
  awaiting_info: 'var(--warning)',
  in_progress: 'var(--accent)',
  resolved: 'var(--success)',
  confirmed: 'var(--success)',
  reopened: 'var(--warning)',
};

function TimelineItem({ entry, index }: { entry: TimelineEntry; index: number }) {
  const Icon = DOT_ICON[entry.status];
  const reveal = useRevealStagger(index);
  return (
    <motion.li key={`${entry.status}-${entry.at}-${index}`} className="timeline-item" {...reveal}>
      <span
        className="timeline-dot"
        aria-hidden="true"
        style={{ borderColor: DOT_COLOR[entry.status], color: DOT_COLOR[entry.status] }}
      >
        <Icon weight="regular" />
      </span>
      <div>
        <div className="timeline-label">{STATUS_LABEL[entry.status]}</div>
        <div className="timeline-time">{absoluteTime(entry.at)}</div>
        {entry.note && <div className="timeline-note">{toCustomerCopy(entry.note)}</div>}
      </div>
    </motion.li>
  );
}

export function StatusTimeline({ entries }: { entries: TimelineEntry[] }) {
  const ordered = [...entries].reverse(); // most recent first
  return (
    <ol className="timeline" aria-label="状态历史">
      {ordered.map((entry, index) => (
        <TimelineItem key={`${entry.status}-${entry.at}-${index}`} entry={entry} index={index} />
      ))}
    </ol>
  );
}
